/**
 * Sesión del panel (owner / staff / superadmin): un único sitio que sabe DÓNDE
 * viven las credenciales y QUÉ hay que borrar al salir.
 *
 * Viven en `sessionStorage` a propósito (petición de Kong, 26-ago-2026): cerrar
 * todas las pestañas o el navegador —y reiniciar el dispositivo— debe cerrar la
 * sesión, y `sessionStorage` muere cuando no queda ninguna pestaña viva. Recargar
 * (F5) NO cierra la sesión (`sessionStorage` sobrevive al reload).
 *
 * Para que abrir una pestaña o ventana nueva mientras YA estás trabajando no pida
 * loguearse otra vez, las pestañas abiertas del mismo navegador se sincronizan por
 * `BroadcastChannel`: la pestaña nueva pide la sesión activa al montarse y, si hay
 * otra pestaña abierta con sesión, la hereda en su propio `sessionStorage`. Si se
 * cierran todas las pestañas o el navegador, no queda nadie al otro lado del canal y
 * al volver a entrar pide login.
 *
 * Las claves de conveniencia (`selectedVenue*`, cachés de IA…) NO son
 * credenciales y siguen en `localStorage`: la tablet debe seguir recordando su
 * tienda. La app de socios tampoco pasa por aquí: `memberToken` (MemberApp)
 * sigue en `localStorage` — el socio en su propio móvil sigue logueado.
 */
import { isCognitoEnabled, cognitoSignOut } from "./cognito";

export const TOKEN_KEY = "kongToken";
export const REFRESH_TOKEN_KEY = "kongRefreshToken";
export const EMAIL_KEY = "kongEmail";
export const CANAL_SESION = "clubsagent_auth_session";

/** Las tres claves que forman una sesión. Nadie más las enumera. */
const CLAVES_CREDENCIALES = [TOKEN_KEY, REFRESH_TOKEN_KEY, EMAIL_KEY];

/** El SDK de Cognito (amazon-cognito-identity-js@6) guarda su propia copia. */
const PREFIJO_SDK_COGNITO = "CognitoIdentityServiceProvider.";

function escribirCredencialesEnSessionStorage({ token, refreshToken, email } = {}) {
  let cambio = false;
  if (token !== undefined && window.sessionStorage.getItem(TOKEN_KEY) !== token) {
    window.sessionStorage.setItem(TOKEN_KEY, token);
    cambio = true;
  }
  if (refreshToken !== undefined && window.sessionStorage.getItem(REFRESH_TOKEN_KEY) !== refreshToken) {
    window.sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    cambio = true;
  }
  if (email !== undefined && window.sessionStorage.getItem(EMAIL_KEY) !== email) {
    window.sessionStorage.setItem(EMAIL_KEY, email);
    cambio = true;
  }
  return cambio;
}

function leerClavesDelSdk(storage) {
  const copia = {};
  try {
    for (let i = 0; i < storage.length; i += 1) {
      const clave = storage.key(i);
      if (clave && clave.startsWith(PREFIJO_SDK_COGNITO)) {
        copia[clave] = storage.getItem(clave);
      }
    }
  } catch {
    // Best-effort si el almacenamiento está bloqueado.
  }
  return copia;
}

function escribirClavesDelSdk(storage, copia) {
  if (!copia || typeof copia !== "object") return;
  try {
    Object.entries(copia).forEach(([clave, valor]) => {
      if (clave.startsWith(PREFIJO_SDK_COGNITO) && typeof valor === "string") {
        storage.setItem(clave, valor);
      }
    });
  } catch {
    // Best-effort.
  }
}

function emitirMensajeCanal(mensaje) {
  if (typeof window === "undefined" || typeof window.BroadcastChannel !== "function") return;
  try {
    const canal = new window.BroadcastChannel(CANAL_SESION);
    canal.postMessage(mensaje);
    canal.close();
  } catch (err) {
    console.warn("[authSession] No se pudo emitir por BroadcastChannel:", err?.message);
  }
}

function construirMensajeSesionActiva() {
  const token = leerToken();
  if (!token) return null;
  return {
    tipo: "sesion-activa",
    token,
    refreshToken: leerRefreshToken(),
    email: leerEmail(),
    cognitoSdk: leerClavesDelSdk(window.sessionStorage),
  };
}

/**
 * Guarda lo que se le pase. Lo que no venga NO se toca: el login local solo
 * tiene token, y `UserContext` reescribe el token sin perder el refresh.
 * Si algún valor cambió, avisa a las demás pestañas abiertas.
 */
export function guardarSesion({ token, refreshToken, email } = {}) {
  const cambio = escribirCredencialesEnSessionStorage({ token, refreshToken, email });
  if (cambio) {
    const mensaje = construirMensajeSesionActiva();
    if (mensaje) emitirMensajeCanal(mensaje);
  }
}

export function leerToken() {
  return window.sessionStorage.getItem(TOKEN_KEY);
}

export function leerRefreshToken() {
  return window.sessionStorage.getItem(REFRESH_TOKEN_KEY);
}

export function leerEmail() {
  return window.sessionStorage.getItem(EMAIL_KEY);
}

/**
 * Borra de `storage` todas las claves del SDK de Cognito. Se listan primero y se
 * borran después: borrar mientras se recorre por índice se salta claves.
 */
function barrerClavesDelSdk(storage) {
  const claves = [];
  for (let i = 0; i < storage.length; i += 1) {
    const clave = storage.key(i);
    if (clave && clave.startsWith(PREFIJO_SDK_COGNITO)) claves.push(clave);
  }
  claves.forEach((clave) => storage.removeItem(clave));
}

/**
 * ¿Hay guardada OTRA sesión distinta de la que hizo una petición? Una respuesta
 * 401/403 que llega tarde, de una petición hecha con el token viejo, no debe cerrar
 * la sesión que acaba de abrirse en la misma pestaña (ahora `cerrarSesion()` borra
 * también el refresh token, y antes solo se limpiaba el estado).
 */
export function otraSesionEnCurso(tokenUsado) {
  const actual = leerToken();
  return Boolean(actual) && actual !== tokenUsado;
}

/**
 * Cierra la sesión de verdad: las tres claves y, con Cognito, también su SDK.
 * Antes cada botón de «Cerrar sesión» borraba solo `kongToken` y dejaba vivo un
 * refresh token de 30 días en el dispositivo.
 * Por defecto avisa a las demás pestañas abiertas para que cierren también.
 */
export function cerrarSesion({ difundir = true } = {}) {
  CLAVES_CREDENCIALES.forEach((clave) => window.sessionStorage.removeItem(clave));
  // `cognitoSignOut()` solo limpia lo que `pool.getCurrentUser()` encuentra por
  // `LastAuthUser`: si esa clave falta, o `getPool()` lanza, el idToken/refreshToken
  // del usuario anterior se quedaban en la pestaña para el siguiente que la use.
  // La app no los lee (el refresh viene de `kongRefreshToken`), pero no deben quedar.
  try {
    barrerClavesDelSdk(window.sessionStorage);
  } catch (err) {
    console.warn("[authSession] No se pudo barrer el SDK de Cognito de sessionStorage:", err?.message);
  }
  // Una copia de antes de este cambio en localStorage no debe poder resucitar la
  // sesión que se acaba de cerrar (la purga del arranque ya la barre, pero este
  // botón no depende de que haya corrido).
  CLAVES_CREDENCIALES.forEach((clave) => window.localStorage.removeItem(clave));
  if (isCognitoEnabled) {
    try {
      cognitoSignOut();
    } catch (err) {
      // Con Cognito mal configurado, `getPool()` lanza. Las claves ya están
      // borradas: que eso no deje al botón de «Cerrar sesión» sin hacer nada.
      console.warn("[authSession] cognitoSignOut falló al cerrar sesión:", err?.message);
    }
  }
  if (difundir) {
    emitirMensajeCanal({ tipo: "sesion-cerrada" });
  }
}

/**
 * Conecta la pestaña actual al canal de sesión entre pestañas:
 * - Si esta pestaña arranca sin token, pregunta si hay otra pestaña abierta con sesión.
 * - Si otra pestaña pide la sesión y esta la tiene, se la envía.
 * - Si otra pestaña inicia/refresca sesión o la cierra, esta pestaña se sincroniza.
 */
export function suscribirSincronizacionPestanas({ onSesionRecibida, onSesionCerrada } = {}) {
  if (typeof window === "undefined" || typeof window.BroadcastChannel !== "function") {
    return () => {};
  }
  let canal;
  try {
    canal = new window.BroadcastChannel(CANAL_SESION);
  } catch (err) {
    console.warn("[authSession] No se pudo abrir BroadcastChannel:", err?.message);
    return () => {};
  }

  canal.onmessage = (event) => {
    const msg = event?.data;
    if (!msg || typeof msg !== "object") return;

    if (msg.tipo === "pedir-sesion") {
      const respuesta = construirMensajeSesionActiva();
      if (respuesta) {
        try {
          canal.postMessage(respuesta);
        } catch (err) {
          console.warn("[authSession] No se pudo responder pedir-sesion:", err?.message);
        }
      }
      return;
    }

    if (msg.tipo === "sesion-activa" && typeof msg.token === "string" && msg.token) {
      escribirCredencialesEnSessionStorage({
        token: msg.token,
        refreshToken: typeof msg.refreshToken === "string" ? msg.refreshToken : undefined,
        email: typeof msg.email === "string" ? msg.email : undefined,
      });
      escribirClavesDelSdk(window.sessionStorage, msg.cognitoSdk);
      if (typeof onSesionRecibida === "function") {
        onSesionRecibida(msg.token);
      }
      return;
    }

    if (msg.tipo === "sesion-cerrada") {
      cerrarSesion({ difundir: false });
      if (typeof onSesionCerrada === "function") {
        onSesionCerrada();
      }
    }
  };

  if (!leerToken()) {
    try {
      canal.postMessage({ tipo: "pedir-sesion" });
    } catch (err) {
      console.warn("[authSession] No se pudo pedir sesión a otras pestañas:", err?.message);
    }
  }

  return () => {
    try {
      canal.close();
    } catch {
      // noop
    }
  };
}

/**
 * Barre las credenciales que quedaron en `localStorage` de antes de este cambio
 * (y las del SDK de Cognito). Se llama una vez al arrancar la app: sin esto, el
 * refresh token de 30 días de cada usuario se quedaría abandonado en su
 * dispositivo tras el deploy.
 */
export function purgarCredencialesLegado() {
  // Con el almacenamiento bloqueado (modo privado estricto, políticas del
  // navegador) cualquier acceso lanza. Corre al arrancar, fuera del
  // ErrorBoundary: un throw aquí sería pantalla en blanco. Es best-effort.
  try {
    CLAVES_CREDENCIALES.forEach((clave) => window.localStorage.removeItem(clave));

    barrerClavesDelSdk(window.localStorage);
  } catch (err) {
    console.warn("[authSession] No se pudo purgar el legado de localStorage:", err?.message);
  }
}
